/**
 * Parse Quill-generated HTML into pdfmake Content objects.
 *
 * Preserves font sizes, alignment, bold/italic/underline, colors, and
 * embedded images from Quill's HTML output — while producing native PDF
 * text (selectable, crisp, no anti-aliasing artifacts).
 *
 * Quill CSS class conventions:
 *   ql-align-center / right / justify → paragraph alignment
 *   ql-size-Npt                       → font size in points
 *   ql-font-*                         → font family (loaded via fontLoader)
 */

import type { Content } from 'pdfmake/interfaces';
import { getFontName, splitTextByFontCoverage } from '../fonts/fontLoader';

/** Inline formatting accumulated while walking the DOM tree */
interface TextSegment {
    text: string;
    bold?: boolean;
    italics?: boolean;
    decoration?: string;
    fontSize?: number;
    color?: string;
    link?: string;
    font?: string;
}

/** Options for parseQuillHtml */
interface ParseQuillHtmlOptions {
    /** Append a trailing spacer after the content (default: true, used for letterhead) */
    trailingSpacer?: boolean;
    /**
     * Read image sizes as CSS lengths and convert them to points (default: false).
     * Off keeps the original unit-blind sizing, which invoices finalized before
     * OWA-835 and non-letterhead HTML must keep.
     */
    cssImageUnits?: boolean;
    /**
     * Size every image at this percent of the text width, keeping its aspect
     * ratio (the letterhead width setting, OWA-835). Unset keeps the image's
     * own size.
     */
    imageWidthPercent?: number;
}

/**
 * Parse Quill HTML into a pdfmake Content array.
 *
 * Each `<p>` in the Quill output becomes one Content item. Inline spans
 * with font size, bold, italic, color etc. become compound text segments.
 * Embedded `<img>` tags become ContentImage items.
 */
export function parseQuillHtml(html: string, options?: ParseQuillHtmlOptions): Content[] {
    if (!html?.trim()) return [];

    const { trailingSpacer = true, cssImageUnits = false, imageWidthPercent } = options || {};

    // Fast path: if the input contains no HTML tags, return a simple text node
    if (!/<[a-zA-Z]/.test(html)) {
        return [{ text: html }];
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const content: Content[] = [];

    for (const child of Array.from(doc.body.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as HTMLElement;
            const tag = el.tagName.toUpperCase();

            // Skip style/script blocks that Quill may include
            if (tag === 'STYLE' || tag === 'SCRIPT') continue;

            content.push(...parseBlock(el, cssImageUnits, imageWidthPercent));
        } else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
            content.push({ text: child.textContent.trim() });
        }
    }

    // Add trailing spacer to separate letterhead from body content
    if (trailingSpacer && content.length > 0) {
        content.push({ text: '', margin: [0, 6, 0, 0] });
    }

    return content;
}

/**
 * Convenience wrapper for parsing HTML content in non-letterhead contexts
 * (e.g. invoice notes, expense descriptions). Omits the trailing spacer.
 */
export function parseHtmlContent(html: string): Content[] {
    return parseQuillHtml(html, { trailingSpacer: false });
}

/**
 * Parse a block element (<p>, <div>, <h*>) into Content items.
 */
function parseBlock(el: HTMLElement, cssImageUnits: boolean, imageWidthPercent?: number): Content[] {
    const items: Content[] = [];
    const alignment = getAlignment(el.className);

    // Handle embedded images — extract them as separate ContentImage items
    const imgs = el.querySelectorAll('img');
    if (imgs.length > 0) {
        for (const img of Array.from(imgs)) {
            try {
                const src = img.getAttribute('src');
                if (!src) continue;

                if (!isValidPdfImage(src)) {
                    warnSkippedImage(src, src.startsWith('data:image/')
                        ? 'unsupported format (pdfmake requires JPEG or PNG)'
                        : src.startsWith('data:') ? 'not an image data URL'
                        : 'external URL (not supported in PDF)');
                    continue;
                }

                const imgContent: Record<string, unknown> = {
                    image: src,
                    alignment: alignment || 'center',
                    margin: [0, 0, 0, 4],
                };

                // Preserve explicit dimensions from HTML attributes or inline style
                const width = parseImageDimension(img, 'width', cssImageUnits);
                const height = parseImageDimension(img, 'height', cssImageUnits);

                if (imageWidthPercent) {
                    // A width alone keeps the aspect ratio in pdfmake.
                    imgContent.width = imageWidthPercent / 100 * CONTENT_WIDTH_PT;
                } else if (width && height) {
                    imgContent.fit = [Math.min(width, 540), height];
                } else if (width) {
                    imgContent.width = Math.min(width, 540);
                } else {
                    // No explicit dimensions — constrain to content area
                    imgContent.fit = [540, 540];
                }

                items.push(imgContent as unknown as Content);
            } catch (err) {
                console.warn('[parseQuillHtml] Error processing image, skipping:', err);
            }
        }

        // Also capture any text siblings of the image(s)
        const segments = applyFontCoverageFallback(extractTextSegments(el, {}));
        if (segments.length > 0 && segments.some(s => s.text.trim())) {
            items.push({
                text: segments.map(toInlineContent),
                alignment,
            } as Content);
        }

        return items;
    }

    // Pure text paragraph — extract all inline segments, then split any
    // segments with custom fonts so unsupported characters fall back to Roboto
    const segments = applyFontCoverageFallback(extractTextSegments(el, {}));

    // Empty paragraph → small vertical spacer (mimics blank line in editor)
    if (segments.length === 0 || !segments.some(s => s.text.trim())) {
        items.push({ text: ' ', fontSize: 6 } as Content);
        return items;
    }

    // Single segment — simple flat content object
    if (segments.length === 1) {
        const seg = segments[0];
        const obj: Record<string, unknown> = { text: seg.text, alignment };
        if (seg.bold) obj.bold = true;
        if (seg.italics) obj.italics = true;
        if (seg.decoration) obj.decoration = seg.decoration;
        if (seg.fontSize) obj.fontSize = seg.fontSize;
        if (seg.color) obj.color = seg.color;
        if (seg.link) obj.link = seg.link;
        if (seg.font) obj.font = seg.font;
        items.push(obj as unknown as Content);
    } else {
        // Multiple segments — compound text array
        items.push({
            text: segments.map(toInlineContent),
            alignment,
        } as Content);
    }

    return items;
}

/**
 * Split segments so characters not covered by the segment's font
 * fall back through the chain: custom font → Roboto → Noto Sans.
 * Applies to ALL fonts including Roboto (which lacks some Unicode symbols).
 */
function applyFontCoverageFallback(segments: TextSegment[]): TextSegment[] {
    const result: TextSegment[] = [];
    for (const seg of segments) {
        const font = seg.font || 'Roboto';
        const parts = splitTextByFontCoverage(seg.text, font);
        for (const part of parts) {
            result.push({ ...seg, text: part.text, font: part.font });
        }
    }
    return result;
}

/**
 * Recursively extract text segments from a DOM subtree,
 * accumulating inline formatting from parent elements.
 */
function extractTextSegments(
    node: Node,
    parentFormat: Partial<TextSegment>
): TextSegment[] {
    const segments: TextSegment[] = [];

    for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === Node.TEXT_NODE) {
            const text = (child.textContent || '').replace(/\uFEFF/g, '');
            if (text) {
                segments.push({ text, ...parentFormat });
            }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child as HTMLElement;
            const tag = el.tagName.toUpperCase();

            // Skip embedded images and metadata
            if (tag === 'IMG' || tag === 'STYLE' || tag === 'SCRIPT') continue;

            // <br> → line break character
            if (tag === 'BR') {
                segments.push({ text: '\n', ...parentFormat });
                continue;
            }

            // Accumulate formatting from this element
            const format: Partial<TextSegment> = { ...parentFormat };

            if (tag === 'STRONG' || tag === 'B') format.bold = true;
            if (tag === 'EM' || tag === 'I') format.italics = true;
            if (tag === 'U') format.decoration = 'underline';
            if (tag === 'S' || tag === 'STRIKE') format.decoration = 'lineThrough';
            if (tag === 'A') format.link = el.getAttribute('href') || undefined;

            // ql-size-Npt → fontSize N
            const sizeMatch = el.className.match(/ql-size-(\d+)pt/);
            if (sizeMatch) {
                format.fontSize = parseInt(sizeMatch[1], 10);
            }

            // ql-font-* → font family (loaded via fontLoader)
            const fontMatch = el.className.match(/ql-font-(\w+)/);
            if (fontMatch) {
                format.font = getFontName(fontMatch[1]);
            }

            // Inline color style
            const color = parseInlineColor(el);
            if (color) format.color = color;

            segments.push(...extractTextSegments(el, format));
        }
    }

    return segments;
}

/** Convert a TextSegment into a pdfmake inline content object */
function toInlineContent(seg: TextSegment): Record<string, unknown> {
    const result: Record<string, unknown> = { text: seg.text };
    if (seg.bold) result.bold = true;
    if (seg.italics) result.italics = true;
    if (seg.decoration) result.decoration = seg.decoration;
    if (seg.fontSize) result.fontSize = seg.fontSize;
    if (seg.color) result.color = seg.color;
    if (seg.link) result.link = seg.link;
    if (seg.font) result.font = seg.font;
    return result;
}

/** Regex matching pdfmake-supported image MIME types (JPEG and PNG only) */
const SUPPORTED_IMAGE_TYPES = /^data:image\/(jpeg|jpg|png);base64,/;

/** Check whether an image src is a valid inline base64 JPEG/PNG for pdfmake */
function isValidPdfImage(src: string): boolean {
    if (!SUPPORTED_IMAGE_TYPES.test(src)) return false;
    const commaIndex = src.indexOf(',');
    if (commaIndex < 0) return false;
    return src.substring(commaIndex + 1).length >= 50;
}

/** Log a diagnostic warning when an image is skipped during PDF generation */
function warnSkippedImage(src: string, reason: string): void {
    const preview = src.length > 80 ? src.substring(0, 80) + '...' : src;
    console.warn(`[parseQuillHtml] Skipping image (${reason}): ${preview}`);
}

/** Extract alignment from Quill paragraph class (ql-align-*) */
function getAlignment(className: string): 'left' | 'center' | 'right' | 'justify' | undefined {
    if (!className) return undefined;
    if (className.includes('ql-align-center')) return 'center';
    if (className.includes('ql-align-right')) return 'right';
    if (className.includes('ql-align-justify')) return 'justify';
    return undefined;
}

/**
 * Parse color from an element's inline style.
 * Handles both `rgb(r,g,b)` and hex `#rrggbb` formats,
 * normalizing to hex for pdfmake.
 */
function parseInlineColor(el: HTMLElement): string | undefined {
    const style = el.getAttribute('style');
    if (!style) return undefined;

    const match = style.match(/(?:^|;)\s*color:\s*([^;]+)/);
    if (!match) return undefined;

    const value = match[1].trim();
    const rgbMatch = value.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
        const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
        const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
        const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }

    return value;
}

/**
 * Parse a dimension (width or height) from an image element.
 * Without cssUnits the attribute is checked first, then the inline style, and
 * the number is used as-is, whatever its unit (the original sizing). With
 * cssUnits the effective inline style wins over the attribute, as in the
 * browser, and the length is converted to points.
 */
function parseImageDimension(img: Element, dim: 'width' | 'height', cssUnits: boolean): number | undefined {
    if (cssUnits) {
        // The style object applies the last declaration and drops !important.
        const styled = (img as HTMLElement).style?.getPropertyValue(dim);
        if (styled) return dimensionToPoints(styled, dim);
        const attr = img.getAttribute(dim);
        return attr ? dimensionToPoints(attr, dim) : undefined;
    }

    // HTML attribute: <img width="200">
    const attr = img.getAttribute(dim);
    if (attr) {
        const num = parseFloat(attr);
        if (!isNaN(num)) return num;
    }

    // Inline style: <img style="width: 200px">
    const style = img.getAttribute('style');
    if (style) {
        const pattern = dim === 'width'
            ? /width:\s*([\d.]+)/
            : /height:\s*([\d.]+)/;
        const match = style.match(pattern);
        if (match) return parseFloat(match[1]);
    }

    return undefined;
}

/** Points per CSS unit; pdfmake sizes are in points (OWA-835). */
const POINTS_PER_UNIT: Record<string, number> = {pt: 1, px: 0.75, in: 72, cm: 72 / 2.54, mm: 72 / 25.4};

/** Width of the letter's text area in points; a percentage width resolves against it. */
const CONTENT_WIDTH_PT = 540;

/**
 * Convert a CSS length to points. A bare number is CSS pixels. A percentage
 * applies to width only, since the page gives a height percentage nothing to
 * resolve against. Unknown units and non-positive values are ignored.
 */
function dimensionToPoints(value: string, dim: 'width' | 'height'): number | undefined {
    const match = value.trim().match(/^(\d*\.?\d+)\s*(%|px|pt|in|cm|mm)?$/i);
    if (!match) return undefined;
    const num = parseFloat(match[1]);
    if (!(num > 0)) return undefined;
    const unit = (match[2] ?? 'px').toLowerCase();
    if (unit === '%') return dim === 'width' ? num / 100 * CONTENT_WIDTH_PT : undefined;
    return num * POINTS_PER_UNIT[unit];
}

/**
 * Read a letterhead width setting as a whole percent from 1 to 100. Anything
 * that is not a finite number means no setting (OWA-835).
 */
export function toImageWidthPercent(value: unknown): number | undefined {
    const num = typeof value === 'string' && value.trim() ? Number(value) : value;
    if (typeof num !== 'number' || !Number.isFinite(num)) return undefined;
    return Math.min(100, Math.max(1, Math.round(num)));
}

export default parseQuillHtml;
